import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IPagedData } from "@features/grid";
import { QueryCommand } from "@features/filter";
import {
    IDashboard,
    IDashboardBuild,
    IDashboardMinimal,
    IDashboardView,
    IModuleDefaultableDashboard
} from "@main/reporting.v3/dashboard/dashboard-models";
import {
    BaseDataService,
    HttpResponsesHandlersFactory,
    IHttpResponseHandlerSettings
} from "@bbwt/modules/data-service";
import { IPublishService } from "@main/reporting.v3/publishing/publish-service";
import { PublishInfo } from "@main/reporting.v3/publishing/publish-info";
import { EntityId } from "@bbwt/interfaces";
import { IOwnerUser } from "../../core/reporting-models";

@Injectable()
export class DashboardService extends BaseDataService implements IPublishService {
    readonly url = "api/reporting3/dashboard";
    readonly entityTitle = "Dashboard";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
    }

    getBuild(dashboardId: string): Promise<IDashboardBuild> {
        return this.httpGet(`${dashboardId}/build`);
    }

    getView(dashboardId: string): Promise<IDashboardView> {
        return this.httpGet(`${dashboardId}/view`);
    }

    getViewByCode(dashboardCode: string): Promise<IDashboardView> {
        return this.httpGet(`${dashboardCode}/view-by-code`);
    }

    getPage(queryCommand: QueryCommand): Promise<IPagedData<IDashboard>> {
        return this.handle<IPagedData<IDashboard>>(
            this.http.get<IPagedData<IDashboard>>(`${this.url}/page`,
                { params: this.constructHttpParams(queryCommand) })
        )
    }

    getAll(): Promise<IDashboardMinimal[]> {
        return this.httpGet();
    }

    create(dashboard: IDashboardBuild): Promise<IDashboardBuild> {
        return this.httpPost(null, dashboard, this.handlersFactory.getForCreate(this.entityTitle));
    }

    update(dashboardId: string, dashboard: IDashboardBuild): Promise<IDashboardBuild> {
        return this.httpPut(`${dashboardId}`, dashboard, this.handlersFactory.getForUpdate(this.entityTitle));
    }

    delete(dashboardId: string): Promise<void> {
        return this.httpDelete(`${dashboardId}`, this.handlersFactory.getForDelete(this.entityTitle));
    }

    getUsersAllowedAsOwners(): Promise<IOwnerUser[]> {
        return this.handle(this.http.get<IOwnerUser[]>(`${this.url}/users-allowed-as-owners`));
    }

    changeOwner(dashboardId: string, ownerUserId: string): Promise<void> {
        return this.httpPut(`${dashboardId}/${ownerUserId}/change-owner`, null,
            this.handlersFactory.getForUpdate(this.entityTitle));
    }

    removeDashboardWidgetDrafts(dashboardId: string): Promise<void> {
        return this.httpDelete(`drafts/${dashboardId}`);
    }

    getPublishInfo(entityId: string, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        return this.httpGet<PublishInfo>(`${entityId}/publish`, this.handlersFactory.getForReadById("Publish", responseHandlerSettings));
    }

    savePublishInfo(entityId: string, publishInfo: PublishInfo, responseHandlerSettings?: IHttpResponseHandlerSettings) {
        return this.httpPut<void>(`${entityId}/publish`, publishInfo, this.handlersFactory.getForUpdate("Publish", responseHandlerSettings));
    }

    getAllModuleDefaultableDashboards(organizationId: EntityId): Promise<IModuleDefaultableDashboard[]> {
        return this.handleRequest(
            this.http.get<IModuleDefaultableDashboard[]>(`${this.url}/${organizationId}/defaultable`),
            this.handlersFactory.getDefault());
    }

    isDefaultAndUniqueSomewhere(dashboardId: string): Promise<boolean> {
        return this.httpGet<boolean>(`${dashboardId}/is-default-and-unique`);
    }

    warningUnpublish(dashboardId: string, publishInfo: PublishInfo) {
        return this.handle<boolean>(
            this.http.post<boolean>(
                `${this.url}/${dashboardId}/warning-unpublish`, publishInfo));
    }
}